import { ReduxState } from '../../../redux/reducers';
import { connect, ConnectedProps } from 'react-redux';
import React from 'react';
import { Empty, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import { Link, useHistory } from 'react-router-dom';
import { FileOutlined, FolderFilled, QuestionCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { Member, MemberRole } from '@quick/client';
import { entrySorter, getFolderEntries, toPath } from '../../../utils/DocumentUtils';
import Utils from '../../../utils';
import classNames from 'classnames';
import { format, Pattern } from '../../../utils/DateFormatUtils';
import { useFolderDetailQuery } from '../../../hooks/Document';
import { track } from '../../../analytics';
import { useDocumentUrl } from '../../../hooks/Routing';
import { useIsAdmin } from '../../../redux/selector';
import { openFile } from '../../../utils/FileRecordUtils';
import MemberAvatar from '../members/MemberAvatar';
import Icon from '../components/Icon';

type FolderEntryListProps = PropsFromRedux & FolderEntryListOwnProps;

interface FolderEntryListOwnProps {
  className?: string;
  selectedEntries: FolderEntry[];
  onSelect: (nodes: FolderEntry[]) => void;
}

export enum EntryType {
  LOCKED_FOLDER,
  FOLDER,
  FILE,
}

export interface FolderEntry {
  type: EntryType;
  name: string;
  member: Member;
  dateModified: Date;
  path: string;
  fileRecordId?: number;
}

const FolderEntryList = ({ selectedEntries, folderNode, onSelect, className }: FolderEntryListProps) => {
  const showCheckbox = useIsAdmin();
  // Fetch data.
  const folderPath = toPath(folderNode);
  const toDocumentUrl = useDocumentUrl();
  const folderDetailQuery = useFolderDetailQuery(folderPath);
  const folderEntries = getFolderEntries(folderDetailQuery.data, toDocumentUrl);
  // Display data.
  const tableColumns: ColumnsType<FolderEntry> = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: entrySorter,
      sortDirections: ['descend'],
      render: (_, node) => <FolderEntryLink node={node} />,
      width: 500,
      ellipsis: true,
    },
    {
      title: 'Date Modified',
      dataIndex: 'dateModified',
      sorter: (a, b) => (a.dateModified?.getTime() ?? 0) - (b.dateModified?.getTime() ?? 0),
      sortDirections: ['ascend', 'descend'],
      render: (_, node) => (
        <Tooltip title={format(node.dateModified, Pattern.DATE_TIME)}>
          <Text className="paragraph-s">{format(node.dateModified)}</Text>
        </Tooltip>
      ),
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Owner',
      dataIndex: 'member',
      render: (_, node) => <MemberAvatar member={node.member} size={32} />,
      width: 200,
    },
  ];
  const selectedRowKeys = selectedEntries.map((it) => it.name);
  const onChange = (selectedKeys: React.Key[], selectedRows: FolderEntry[]) => {
    onSelect(selectedRows);
  };
  return (
    <>
      <Table
        className={classNames(className)}
        columns={tableColumns}
        dataSource={folderEntries}
        rowKey={(folderEntry) => folderEntry.name}
        rowSelection={showCheckbox ? { selectedRowKeys, onChange } : undefined}
        pagination={false}
        scroll={{ x: 'max-content', y: Utils.getTableScroll({}) }}
        locale={{
          emptyText: (
            <Empty
              className="m-5"
              description={
                <div>
                  <div className="label-l text-content-primary">Folder is empty</div>
                  <div className="text-content-primary">Drag and drop files onto this window to upload</div>
                </div>
              }
            />
          ),
        }}
      />
    </>
  );
};

interface FolderEntryLinkProps {
  node: FolderEntry;
}

const FolderEntryLink: React.FC<FolderEntryLinkProps> = ({ node }) => {
  const history = useHistory();
  const isFile = node.type === EntryType.FILE;
  const isLockedFolder = node.type === EntryType.LOCKED_FOLDER;
  const icon = isFile ? (
    <FileOutlined className="font-size-heading-s mr-2 text-primary" />
  ) : isLockedFolder ? (
    <FolderLocked />
  ) : (
    <FolderFilled className="font-size-heading-s mr-2 text-primary" />
  );
  return (
    <Link
      to="#"
      onClick={async () => {
        if (!isFile) {
          history.push(node.path);
          track('UI/DOCUMENTS/FOLDER/CLICK');
        } else {
          if (!node.fileRecordId) {
            return;
          }
          track('UI/DOCUMENTS/FILE/CLICK', { fileRecordId: node.fileRecordId });
          openFile(node.fileRecordId);
        }
      }}
      draggable={false}
      className="text-content-primary"
    >
      <div className="d-flex flex-row align-items-center paragraph-s">
        {icon} {node.name} {isLockedFolder && <LockedFolderTooltip />}
      </div>
    </Link>
  );
};

const FolderLocked = () => {
  return (
    <div className="mr-2" style={{ width: '24px', height: '24px' }}>
      <Icon height="100%" width="100%" preserveAspectRatio="xMinYMin slice" viewBox="3 3 30 30" name="folder-locked" />
    </div>
  );
};

export const LockedFolderTooltip = () => {
  return (
    <Tooltip title="Only community admins (board members and managers) can see this folder">
      <QuestionCircleOutlined className="ml-2" />
    </Tooltip>
  );
};

const mapStateToProps = (state: ReduxState, props: FolderEntryListOwnProps) => {
  return {
    ...props,
    folderNode: state.document.activeFolderNode,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(FolderEntryList);
type PropsFromRedux = ConnectedProps<typeof connector>;
